import { ChangeDetectionStrategy, Component, signal, OnInit, OnDestroy, computed } from '@angular/core';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { DashboardDataService } from '../../../services/dashboard-data.service';
import { KpiService } from 'src/app/api/kpi.service';
import { KpiReportService } from 'src/app/api/kpiReport.service';
import { Subject, takeUntil } from 'rxjs';
import { StaffRoles } from 'src/app/auth/tokens';
import { StaffKpiData } from 'src/app/api/types/kpi';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrl: './home.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    private ngUnsubscribe = new Subject<void>();
    
    currentStaffId = signal<number>(0);
    dataPadOpenSplit = [66, 33];
    minDatapadSizes = [50, 0.2];
    splitterPanelSizes = signal<number[]>([99, 0.2]);
    isSupervisor = computed(() => this.kpiService.isSupervisor());
    hasSupervisees = computed(() => this.kpiService.hasSupervisees());
    kpiData = signal<StaffKpiData | null>(null);
    
    // Role-based computed properties
    isLeadership = computed(() => {
        const user = this.auth.currentUser;
        return user ? (this.auth.hasRole('it') || this.hasStaffId([106, 26])) : false;
    });
    
    hasKpis = computed(() => {
        // Check if user has actual KPIs (not just skill/self assessments)
        // This matches the ColdFusion logic: only show if there are real KPIs
        const data = this.kpiData();
        if (!data || !data.kpis || data.kpis.length === 0) {
            return false;
        }
        
        // Filter out skill assessments and self assessments (unless in YER mode)
        // This matches ColdFusion: (getStaffKpis.kpi NEQ 'skillAssessment' AND getStaffKpis.kpi NEQ 'selfAssessment')
        const realKpis = data.kpis.filter(kpi => 
            kpi.kpi !== 'skillAssessment' && kpi.kpi !== 'selfAssessment'
        );
        
        return realKpis.length > 0;
    });
    
    canEditPpmGoals = computed(() => {
        return this.auth.hasAnyRole(['it', 'salesAdmin']);
    });
    
    canAddBulletins = computed(() => {
        return this.auth.hasAnyRole(['head', 'it']);
    });

    constructor(
        private auth: LocalAuthService,
        public dashboardDataService: DashboardDataService,
        private kpiService: KpiService,
        private kpiReportService: KpiReportService
    ) {}

    ngOnInit() {
        // Set current staff ID
        this.currentStaffId.set(this.auth.currentUser?.newstaffId ?? 0);
       

        // Initialize dashboard data
        this.dashboardDataService.initializeData();
        
        // Load KPI data to check if user has real KPIs
        if (this.currentStaffId() > 0) {
            this.kpiReportService.getKpiReportForStaff(this.currentStaffId(), false)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(data => {
                    this.kpiData.set(data);
                });
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    dataPadCountChange(count: number) {
        if (count === 0) {
            this.splitterPanelSizes.set([99, 1]);
        } else {
            this.splitterPanelSizes.set(this.dataPadOpenSplit);
        }
    }
    
    // Role checking helper methods
    hasStaffId(staffIds: number[]): boolean {
        const user = this.auth.currentUser;
        return user ? staffIds.includes(user.newstaffId) : false;
    }
    
    hasEmail(emails: string[]): boolean {
        const user = this.auth.currentUser;
        return user ? emails.includes(user.email) : false;
    }
}
